// src/pages/DiscoverPage.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Lock,
  Heart,
  MapPin,
  SlidersHorizontal,
  Search,
  UserCircle,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import {
  calculateAge,
  canAccessDating,
  getDisplayAge,
  getFirstBioPrompt,
  getDistance,
  getCommonInterests,
  calculateCompatibility,
} from '../utils';
import { INTERESTS } from '../constants';
import { Btn, Card, Badge, EmptyState, ErrorState, Infobulle } from '../components/ui';
import FiltersModal from '../modals/FiltersModal';
import ProfileModal from '../modals/ProfileModal';
import MatchModal from '../modals/MatchModal';

const DiscoverPage = ({ user, openAuth, toast, onUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [likedIds, setLikedIds] = useState(user?.likes || []);
  const [filters, setFilters] = useState({
    ageMin: user?.datingPreferences?.ageRange?.[0] || '',
    ageMax: user?.datingPreferences?.ageRange?.[1] || '',
    city: '',
    clan: '',
    intention: '',
    gender: '',
    distance: user?.datingPreferences?.maxDistance || '',
    noDistanceLimit: !user?.datingPreferences?.maxDistance,
    intentions: user?.datingPreferences?.intentions || [],
    verifiedOnly: false,
    onlineOnly: false,
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const userRef = useRef(user);
  userRef.current = user;
  const initialLoadDone = useRef(false);

  const prevLikesCount = useRef(user?.likes?.length || 0);

  // Synchroniser likedIds quand user change
  useEffect(() => {
    if (user?.likes) setLikedIds(user.likes);
  }, [user]);

  const load = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      const raw = await userService.getProfiles(userRef.current, filtersRef.current);
      // Score et tri par compatibilité
      const scored = raw
        .map((p) => ({
          ...p,
          _compat: calculateCompatibility(userRef.current, p),
        }))
        .sort((a, b) => b._compat.score - a._compat.score);
      setProfiles(scored);
    } catch (err) {
      console.error('DiscoverPage load error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !initialLoadDone.current) {
      initialLoadDone.current = true;
      load();
    }
  }, [user, load]);

  // Recharger quand un unlike a eu lieu (likes count a diminué)
  useEffect(() => {
    const currentCount = user?.likes?.length || 0;
    if (initialLoadDone.current && currentCount < prevLikesCount.current) {
      load();
    }
    prevLikesCount.current = currentCount;
  }, [user?.likes?.length, load]);

  const handleLike = async (id, e) => {
    e?.stopPropagation();

    // Optimistic UI : mettre à jour immédiatement le cœur + retirer le profil de la liste
    const alreadyLiked = likedIds.includes(id);
    setLikedIds((prev) => (alreadyLiked ? prev.filter((x) => x !== id) : [...prev, id]));
    const likedProfile = !alreadyLiked ? profiles.find((p) => p.uid === id) : null;
    if (!alreadyLiked) {
      setProfiles((prev) => prev.filter((p) => p.uid !== id));
    }

    const r = await userService.like(user.uid, id);

    if (r.success) {
      // Rafraîchir l'état user complet depuis Firestore (silencieux, pas de re-fetch profils)
      const fresh = await userService.getUser();
      if (fresh && onUpdate) onUpdate(fresh);

      if (r.match) {
        // Match détecté ! Afficher la célébration
        setMatchInfo({ name: r.matchName, profile: likedProfile });
      } else {
        toast(r.msg, 'success');
      }
    } else {
      // Rollback optimistic update si erreur
      console.error('handleLike failed:', r);
      setLikedIds((prev) => (alreadyLiked ? [...prev, id] : prev.filter((x) => x !== id)));
      if (likedProfile) {
        setProfiles((prev) => [...prev, likedProfile]);
      }
      toast(t('discover.toast.error'), 'error');
    }
  };

  const isLiked = (id) => likedIds.includes(id);

  // Fallback informatif pour les mineurs
  if (user && !canAccessDating(user)) {
    const age = calculateAge(user.dateOfBirth);
    if (age !== null && age < 18) {
      return (
        <div
          className="max-w-md mx-auto text-center py-20 px-4"
          data-testid="discover-minor-message"
        >
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('discover.minor.title')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{t('discover.minor.desc')}</p>
          <Btn onClick={() => navigate('/events')}>{t('discover.minor.cta')}</Btn>
        </div>
      );
    }
  }

  // Écran non connecté
  if (!user) {
    return (
      <div className="pt-32 pb-32 container mx-auto px-4">
        <Card className="max-w-md mx-auto p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto mb-4">
            <Lock size={28} className="text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 dark:text-gray-100">
            {t('discover.guest.title')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{t('discover.guest.desc')}</p>
          <Btn onClick={openAuth} className="w-full" data-testid="discover-btn-auth">
            {t('discover.guest.cta')}
          </Btn>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-100 dark:from-rose-950/[0.35] via-rose-50/50 dark:via-rose-950/[0.15] to-rose-50/10 dark:to-transparent">
      <div className="filigrane-kotaw pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <Helmet>
          <title>{t('discover.meta.title')}</title>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://hmonghearts.com/discover" />
        </Helmet>
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold dark:text-gray-100">
              {t('discover.title')}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
              {profiles.length > 0
                ? t('discover.subtitle')
                : t(
                    profiles.length > 1 ? 'discover.profileCount_plural' : 'discover.profileCount',
                    { count: profiles.length }
                  )}
            </p>
          </div>
          <div className="flex gap-2">
            <Btn
              v="outline"
              size="sm"
              icon={SlidersHorizontal}
              onClick={() => setShowFilters(true)}
              data-testid="discover-btn-filters"
            >
              <span className="hidden sm:inline">{t('discover.filters')}</span>
              <span className="sm:hidden">{t('discover.filters')}</span>
            </Btn>
            <Btn
              v="ghost"
              size="sm"
              icon={Search}
              onClick={load}
              data-testid="discover-btn-refresh"
            >
              <span className="hidden sm:inline">{t('discover.refresh')}</span>
            </Btn>
          </div>
        </div>

        {user.clan && user.clan !== 'Autre' && (
          <Infobulle type="info" storageKey="hmh-hint-discover-clan">
            {t('discover.clanHint', { clan: user.clan })}
          </Infobulle>
        )}

        {error ? (
          <ErrorState onRetry={load} />
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm"
              >
                <div className="relative h-72 sm:h-80 animate-pulse bg-gray-200 dark:bg-gray-700">
                  <div className="absolute bottom-0 p-5 w-full space-y-2">
                    <div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded-full" />
                    <div className="h-6 w-40 bg-gray-300 dark:bg-gray-600 rounded-lg" />
                    <div className="h-4 w-28 bg-gray-300 dark:bg-gray-600 rounded-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t('discover.empty.title')}
            desc={t('discover.empty.desc')}
            ctaLabel={t('discover.empty.cta')}
            onCtaClick={() => setShowFilters(true)}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {profiles.map((p, index) => {
              const age = getDisplayAge(p);
              const firstBio = getFirstBioPrompt(p);
              const dist = getDistance(user, p);
              const common = getCommonInterests(user, p, INTERESTS).slice(0, 2);
              return (
                <Card
                  key={p.uid}
                  data-testid="discover-card"
                  className="overflow-hidden group animate-fade-in-up"
                  style={{ animationDelay: `${index * 60}ms` }}
                  hover
                  onClick={() => setSelected(p)}
                >
                  <div className="relative h-72 sm:h-80">
                    {p.profilePicture ? (
                      <img
                        src={p.profilePicture}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <UserCircle size={64} className="text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    {p.online && (
                      <div className="absolute top-3 left-3">
                        <Badge c="green">{t('common.online')}</Badge>
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      {p._compat && p._compat.score > 70 ? (
                        <span className="inline-flex items-center gap-1 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          <Sparkles size={12} />
                          {t('discover.compatible', { score: p._compat.score })}
                        </span>
                      ) : p.verified ? (
                        <Badge c="blue">{t('common.verified')}</Badge>
                      ) : null}
                    </div>
                    <div className="absolute bottom-0 p-5 text-white w-full">
                      {p.clan && <Badge c="rose">{p.clan}</Badge>}
                      <h3 className="text-base sm:text-2xl font-bold mt-2">
                        {p.name}
                        {age !== null ? `, ${age}` : ''}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-white/80">
                        <MapPin size={14} />
                        {p.city}
                        {dist !== null && ` · à ${dist} km`}
                      </div>
                      {common.length > 0 && (
                        <div className="flex gap-1.5 mt-1.5 mb-1">
                          {common.map((c) => (
                            <span
                              key={c.value}
                              className="text-xs bg-white/20 px-2 py-0.5 rounded-full"
                            >
                              {c.labelKey ? t(c.labelKey) : c.value}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-white/70 line-clamp-2 mb-3 mt-1">
                        {firstBio?.answer || ''}
                      </p>
                      <div className="flex gap-2">
                        <Btn
                          v="white"
                          className="flex-1 text-sm"
                          data-testid="discover-card-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(p);
                          }}
                        >
                          {t('common.viewProfile')}
                        </Btn>
                        <button
                          data-testid="discover-card-like"
                          onClick={(e) => handleLike(p.uid, e)}
                          aria-label={
                            isLiked(p.uid) ? `Ne plus aimer ${p.name}` : `Aimer ${p.name}`
                          }
                          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                            isLiked(p.uid)
                              ? 'bg-rose-500 text-white animate-like-bounce'
                              : 'bg-white/20 text-white hover:bg-rose-500'
                          }`}
                        >
                          <Heart size={18} fill={isLiked(p.uid) ? 'white' : 'none'} />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <FiltersModal
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          setFilters={setFilters}
          onApply={() => {
            setShowFilters(false);
            load();
          }}
          onReset={() => {
            const prefs = user?.datingPreferences || {};
            setFilters({
              ageMin: prefs.ageRange?.[0] || '',
              ageMax: prefs.ageRange?.[1] || '',
              city: '',
              clan: '',
              intention: '',
              gender: '',
              distance: prefs.maxDistance || '',
              noDistanceLimit: !prefs.maxDistance,
              intentions: prefs.intentions || [],
              verifiedOnly: false,
              onlineOnly: false,
            });
          }}
          userClan={user.clan}
        />
        <ProfileModal
          isOpen={!!selected}
          onClose={() => setSelected(null)}
          profile={selected}
          context="dating"
          onLike={() => handleLike(selected?.uid)}
          hasLiked={selected ? isLiked(selected.uid) : false}
          toast={toast}
          currentUser={user}
          onBlock={(blockedId) => {
            setSelected(null);
            setProfiles((prev) => prev.filter((p) => p.uid !== blockedId));
          }}
        />
        <MatchModal
          isOpen={!!matchInfo}
          onClose={() => setMatchInfo(null)}
          matchName={matchInfo?.name}
          matchProfile={matchInfo?.profile}
          currentUser={user}
        />
      </div>
    </div>
  );
};

export default DiscoverPage;
